import {
  fetchMarketdataOptionPopBatch,
  getCachedMarketdataOptionPop,
} from "../api/marketdataOptionPop";
import {
  buildRecommendationLookup,
  buildShareAvgByTicker,
  callRiskOptyExposure,
  callRiskOptyValueClass,
  callStrikeVsAvgCost,
  callStrikeVsAvgTip,
  callStrikeTipHostClass,
  callStrikeVsAvgValueClass,
  renderCallStrikeTipPopover,
  fmtUsd,
  fmtUsdWhole,
  fmtWheelRatio,
  loadPositionsSnapshot,
  loadRecommendationsSnapshot,
  matchPositionToRecommendation,
  normalizeTicker,
  type OptionPosition,
  type WheelRecommendation,
} from "./watchlistData";
import { WHEEL_LEDGER_CHANGED_EVENT } from "./pageBridge";
import { syncResetButtons } from "./filterReset";
import { paintScreenerExpiryCell } from "./screenerExpiryCell";
import { isWebCompactMode } from "./webCompactMode";

const AVG_COST_HEADER = "sfe-avg-cost-header";
const AVG_COST_CELL = "sfe-avg-cost-cell";
const RISK_OPTY_HEADER = "sfe-risk-opty-header";
const RISK_OPTY_CELL = "sfe-risk-opty-cell";
const POP_HEADER = "sfe-pop-header";
const POP_CELL = "sfe-pop-cell";
const MARKETDATA_POP_BATCH_SIZE = 200;
const EXPIRATION_FILTER_ID = "sfe-options-expiration-filter";
const EXPIRATION_FILTER_HIDDEN = "sfe-expiration-filter-hidden";
const RISK_FILTER_ID = "sfe-options-risk-filter";
const RISK_FILTER_HIDDEN = "sfe-risk-filter-hidden";

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let domObserver: MutationObserver | null = null;
let applying = false;
let expirationFilter: "all" | "expired" | "at-expiration" = "all";
let riskFilter: "all" | "risky" = "all";

function findWheelPositionsTable(): HTMLTableElement | null {
  const table = document.getElementById("wheelPositionsTable");
  return table instanceof HTMLTableElement ? table : null;
}

function headerRow(table: HTMLTableElement): HTMLTableRowElement | null {
  const row = table.querySelector("thead tr");
  return row instanceof HTMLTableRowElement ? row : null;
}

function headerColumnIndex(table: HTMLTableElement, sortKey: string): number {
  const row = headerRow(table);
  if (!row) return -1;
  for (let i = 0; i < row.children.length; i++) {
    if (row.children[i].getAttribute("data-sort") === sortKey) return i;
  }
  return -1;
}

function avgCostColumnIndex(table: HTMLTableElement): number {
  const row = headerRow(table);
  if (!row) return -1;
  for (let i = 0; i < row.children.length; i++) {
    if (row.children[i].classList.contains(AVG_COST_HEADER)) return i;
  }
  return -1;
}

function riskOptyColumnIndex(table: HTMLTableElement): number {
  const row = headerRow(table);
  if (!row) return -1;
  for (let i = 0; i < row.children.length; i++) {
    if (row.children[i].classList.contains(RISK_OPTY_HEADER)) return i;
  }
  return -1;
}

function isPositionsPlaceholderRow(row: HTMLTableRowElement): boolean {
  const only = row.querySelector("td[colspan]");
  return only instanceof HTMLTableCellElement && row.children.length === 1;
}

function findStrikeHeader(table: HTMLTableElement): HTMLTableCellElement | null {
  const row = headerRow(table);
  if (!row) return null;
  for (const th of row.children) {
    if (th.getAttribute("data-sort") === "strike") {
      return th instanceof HTMLTableCellElement ? th : null;
    }
  }
  return null;
}

function rowCells(row: HTMLTableRowElement): HTMLTableCellElement[] {
  return Array.from(row.querySelectorAll("td, th")).filter(
    (cell): cell is HTMLTableCellElement => cell instanceof HTMLTableCellElement,
  );
}

function bodyColumnIndex(
  table: HTMLTableElement,
  row: HTMLTableRowElement,
  headerIndex: number,
): number {
  const headers = headerRow(table);
  if (!headers || headerIndex < 0) return headerIndex;

  const cells = rowCells(row);
  if (cells.length === headers.children.length) return headerIndex;

  const avgCol = avgCostColumnIndex(table);
  if (avgCol >= 0 && headerIndex > avgCol && !row.querySelector(`.${AVG_COST_CELL}`)) {
    return headerIndex - 1;
  }
  return headerIndex;
}

function findBodyCellBySort(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
  sortKey: string,
): HTMLTableCellElement | null {
  const hIdx = headerColumnIndex(table, sortKey);
  if (hIdx < 0) return null;
  const index = bodyColumnIndex(table, row, hIdx);
  return rowCells(row)[index] ?? null;
}

function findBodyStrikeCell(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
): HTMLTableCellElement | null {
  const avgCell = row.querySelector(`.${AVG_COST_CELL}`);
  if (avgCell?.nextElementSibling instanceof HTMLTableCellElement) {
    return avgCell.nextElementSibling;
  }

  const byCol = row.querySelector('td[data-col="strike"]');
  if (byCol instanceof HTMLTableCellElement) return byCol;

  const tickerCell =
    row.querySelector('td[data-col="underlying_ticker"]') ??
    row.querySelector("td.tk-cell");
  if (tickerCell instanceof HTMLTableCellElement) {
    const sectorCell = tickerCell.nextElementSibling;
    if (sectorCell instanceof HTMLTableCellElement) {
      const strikeCell = sectorCell.nextElementSibling;
      if (strikeCell instanceof HTMLTableCellElement) return strikeCell;
    }
  }

  const strikeHeaderIndex = headerColumnIndex(table, "strike");
  if (strikeHeaderIndex < 0) return null;
  const index = bodyColumnIndex(table, row, strikeHeaderIndex);
  return rowCells(row)[index] ?? null;
}

function ensureAvgCostCell(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
): HTMLTableCellElement | null {
  const avgCol = avgCostColumnIndex(table);
  if (avgCol < 0) return null;

  let avgCell = row.querySelector<HTMLTableCellElement>(`.${AVG_COST_CELL}`);
  if (avgCell instanceof HTMLTableCellElement) {
    const strikeCell = findBodyStrikeCell(row, table);
    if (strikeCell && avgCell.nextElementSibling !== strikeCell) {
      row.insertBefore(avgCell, strikeCell);
    }
    return avgCell;
  }

  const strikeCell = findBodyStrikeCell(row, table);
  if (!strikeCell) return null;

  avgCell = document.createElement("td");
  avgCell.className = `num ${AVG_COST_CELL}`;
  avgCell.setAttribute("data-col", "sfe_holding_avg_cost");
  row.insertBefore(avgCell, strikeCell);
  return avgCell;
}

function ensureRiskOptyCell(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
): HTMLTableCellElement | null {
  if (riskOptyColumnIndex(table) < 0) return null;

  let cell = row.querySelector<HTMLTableCellElement>(`.${RISK_OPTY_CELL}`);
  const strikeCell = findBodyStrikeCell(row, table);
  if (!strikeCell) return null;

  if (cell instanceof HTMLTableCellElement) {
    if (strikeCell.nextElementSibling !== cell) {
      strikeCell.insertAdjacentElement("afterend", cell);
    }
    return cell;
  }

  cell = document.createElement("td");
  cell.className = `num ${RISK_OPTY_CELL}`;
  cell.setAttribute("data-col", "sfe_risk_opty");
  strikeCell.insertAdjacentElement("afterend", cell);
  return cell;
}

function paintRiskOptyCell(
  cell: HTMLTableCellElement,
  side: string | undefined,
  strike: number | undefined,
  avgCost: number | undefined,
  contracts: number | undefined,
): void {
  cell.classList.remove("roi-loss", "roi-strong", "muted", "sfe-data-tip-host");
  cell.removeAttribute("data-tip");
  const exposure = callRiskOptyExposure(side, strike, avgCost, contracts);
  if (!exposure) {
    cell.classList.add("muted");
    cell.textContent = "—";
    return;
  }
  const tone = callRiskOptyValueClass(exposure.kind);
  if (tone) cell.classList.add(tone);
  cell.classList.add("sfe-data-tip-host");
  cell.setAttribute(
    "data-tip",
    exposure.kind === "risk"
      ? "Assignment risk vs share avg cost: (avg cost − strike) × shares."
      : "Upside vs share avg cost if assigned: (strike − avg cost) × shares.",
  );
  cell.innerHTML = fmtUsdWhole(exposure.kind === "risk" ? -exposure.amount : exposure.amount);
}

const MONEYNESS_RE = /^(OTM|ITM|ATM)$/i;

function parseRowTicker(row: HTMLTableRowElement, table: HTMLTableElement): string {
  const cell = findBodyCellBySort(row, table, "ticker");
  if (!cell) return "";

  // Prefer the real ticker link/node — moneyness badges often sit beside it
  // with no whitespace ("OTMQCOM").
  const link = cell.querySelector("a, .ticker, .symbol, [data-ticker], .recs-ticker");
  if (link) {
    const linkText = (link.getAttribute("data-ticker") ?? link.textContent ?? "")
      .trim()
      .toUpperCase();
    const fromLink = linkText.match(/^([A-Z]{1,6})\b/);
    if (fromLink) return fromLink[1];
  }

  const clone = cell.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll(
      ".badge, .pill, .tag, [class*='otm'], [class*='itm'], [class*='atm'], [class*='money']",
    )
    .forEach((el) => el.remove());
  for (const el of Array.from(clone.querySelectorAll("span, small, i, em, b, strong"))) {
    if (MONEYNESS_RE.test((el.textContent ?? "").trim())) el.remove();
  }

  let text = (clone.textContent ?? "").replace(/\s+/g, " ").trim().toUpperCase();
  text = text.replace(/^(OTM|ITM|ATM)\s+/, "").replace(/^(OTM|ITM|ATM)(?=[A-Z]{1,6}\b)/, "");
  const match = text.match(/^([A-Z]{1,6})\b/);
  return match ? match[1] : "";
}

/** Best-effort underlying from an OCC option symbol (e.g. QCOM260717C00150000). */
function tickerFromOptionSymbol(symbol: string): string {
  const match = symbol.trim().toUpperCase().match(/^([A-Z]{1,6})(?=\d{6}[CP])/);
  return match?.[1] ?? "";
}

function isCallRowDom(row: HTMLTableRowElement, table: HTMLTableElement): boolean {
  const sideCell = findBodyCellBySort(row, table, "side");
  const text = (sideCell?.textContent ?? "").toLowerCase();
  if (text.includes("put")) return false;
  if (text.includes("call")) return true;
  return row.querySelector(".side-call") != null;
}

function positionsByOptionSymbol(positions: OptionPosition[]): Map<string, OptionPosition> {
  const map = new Map<string, OptionPosition>();
  for (const p of positions) {
    if (p.option_symbol) map.set(p.option_symbol, p);
  }
  return map;
}

function directText(element: Element): string {
  return Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent ?? "")
    .join(" ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

const EXPIRATION_TIP =
  "All open positions, already-expired contracts, or contracts expiring within 3 days (DTE 0–2).";

const RISK_TIP =
  "Risky shows short calls with strike below your average share cost — assignment would lock in a loss.";

function buildSegmentFilterHost(
  id: string,
  hostClass: string,
  label: string,
  tip: string,
  ariaLabel: string,
  dataAttr: string,
  values: readonly string[],
  labels: readonly string[],
  activeValue: string,
  onSelect: (value: string) => void,
  template: HTMLElement | null,
): HTMLElement {
  const labelClass = `${hostClass}-label`;
  const segmentsClass = `${hostClass}-segments`;

  if (template) {
    const host = template.cloneNode(true) as HTMLElement;
    host.id = id;
    host.classList.add(hostClass);
    host.setAttribute("data-sfe-injected", "true");
    host.removeAttribute("data-filter");
    host.removeAttribute("data-col");

    const labelNode =
      Array.from(host.querySelectorAll<HTMLElement>("label, span, div")).find(
        (el) => directText(el) === "MONEYNESS",
      ) ?? null;
    if (labelNode) {
      const textNode = Array.from(labelNode.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim(),
      );
      if (textNode) textNode.textContent = label;
      else labelNode.textContent = label;
    }

    const hint =
      host.querySelector<HTMLElement>(".hint, [data-tip], [title]") ??
      (() => {
        const el = document.createElement("span");
        el.className = "hint";
        el.tabIndex = 0;
        el.setAttribute("role", "img");
        el.textContent = "i";
        labelNode?.appendChild(el);
        return el;
      })();
    hint.classList.add("hint");
    hint.setAttribute("role", "img");
    hint.tabIndex = 0;
    hint.setAttribute("data-tip", tip);
    hint.removeAttribute("title");
    if (!hint.textContent?.trim()) hint.textContent = "i";

    const buttons = Array.from(host.querySelectorAll("button"));
    while (buttons.length < values.length) {
      const clone = (buttons[0] ?? document.createElement("button")).cloneNode(true) as HTMLButtonElement;
      clone.type = "button";
      buttons[0]?.parentElement?.appendChild(clone);
      buttons.push(clone);
    }
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      if (i >= values.length) {
        button.remove();
        continue;
      }
      button.type = "button";
      button.textContent = labels[i];
      button.setAttribute(`data-${dataAttr}`, values[i]);
      button.removeAttribute("data-filter");
      button.removeAttribute("data-side");
      button.removeAttribute("data-mny");
      button.removeAttribute("data-expiration-filter");
      button.removeAttribute("data-risk-filter");
      button.setAttribute(`data-${dataAttr}`, values[i]);
      button.classList.toggle("active", values[i] === activeValue);
      button.setAttribute("aria-pressed", String(values[i] === activeValue));
    }

    host.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        `[data-${dataAttr}]`,
      );
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      onSelect(button.dataset[dataAttrToDatasetKey(dataAttr)] ?? "all");
    });

    return host;
  }

  const host = document.createElement("div");
  host.id = id;
  host.className = hostClass;
  host.setAttribute("data-sfe-injected", "true");
  host.innerHTML = `
    <div class="${labelClass}">
      <span>${label}</span>
      <span class="hint" tabindex="0" role="img" data-tip="${tip}">i</span>
    </div>
    <div class="${segmentsClass}" role="group" aria-label="${ariaLabel}">
      ${values
        .map(
          (value, i) =>
            `<button type="button"${value === activeValue ? ' class="active"' : ""} data-${dataAttr}="${value}" aria-pressed="${value === activeValue}">${labels[i]}</button>`,
        )
        .join("")}
    </div>`;
  host.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      `[data-${dataAttr}]`,
    );
    if (!button) return;
    onSelect(button.dataset[dataAttrToDatasetKey(dataAttr)] ?? "all");
  });
  return host;
}

function dataAttrToDatasetKey(dataAttr: string): string {
  return dataAttr.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
}

function syncSegmentFilterButtons(
  host: HTMLElement,
  dataAttr: string,
  activeValue: string,
): void {
  for (const button of host.querySelectorAll<HTMLButtonElement>(`[data-${dataAttr}]`)) {
    const value = button.dataset[dataAttrToDatasetKey(dataAttr)];
    const active = value === activeValue;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  }
}

function buildExpirationSelectFilter(sectorTemplate: HTMLElement | null): HTMLElement {
  const values = ["all", "expired", "at-expiration"] as const;
  const labels = ["All", "Expired", "At expiration"] as const;

  const applySelect = (select: HTMLSelectElement): void => {
    select.classList.add("sfe-options-expiration-select");
    select.setAttribute("aria-label", "Filter options by expiration");
    select.innerHTML = values
      .map((value, i) => `<option value="${value}">${labels[i]}</option>`)
      .join("");
    select.value = expirationFilter;
    select.addEventListener("change", () => {
      const value = select.value;
      expirationFilter =
        value === "expired" || value === "at-expiration" ? value : "all";
      scheduleSync();
    });
  };

  if (sectorTemplate) {
    const host = sectorTemplate.cloneNode(true) as HTMLElement;
    host.id = EXPIRATION_FILTER_ID;
    host.classList.add("sfe-options-expiration-filter", "sfe-options-expiration-combo");
    host.setAttribute("data-sfe-injected", "true");
    host.removeAttribute("data-filter");
    host.removeAttribute("data-col");

    const labelNode =
      Array.from(host.querySelectorAll<HTMLElement>("label, span, div")).find(
        (el) => directText(el) === "SECTOR",
      ) ?? null;
    if (labelNode) {
      const textNode = Array.from(labelNode.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim(),
      );
      if (textNode) textNode.textContent = "Expiration";
      else labelNode.textContent = "Expiration";
    }

    const hint =
      host.querySelector<HTMLElement>(".hint, [data-tip], [title]") ??
      (() => {
        const el = document.createElement("span");
        el.className = "hint";
        el.tabIndex = 0;
        el.setAttribute("role", "img");
        el.textContent = "i";
        labelNode?.appendChild(el);
        return el;
      })();
    hint.classList.add("hint");
    hint.setAttribute("role", "img");
    hint.tabIndex = 0;
    hint.setAttribute("data-tip", EXPIRATION_TIP);
    hint.removeAttribute("title");
    if (!hint.textContent?.trim()) hint.textContent = "i";

    const select = host.querySelector("select");
    if (select instanceof HTMLSelectElement) {
      applySelect(select);
      return host;
    }
  }

  const host = document.createElement("div");
  host.id = EXPIRATION_FILTER_ID;
  host.className = "sfe-options-expiration-filter sfe-options-expiration-combo";
  host.setAttribute("data-sfe-injected", "true");
  host.innerHTML = `
    <div class="sfe-options-expiration-label">
      <span>Expiration</span>
      <span class="hint" tabindex="0" role="img" data-tip="${EXPIRATION_TIP}">i</span>
    </div>
    <select class="sfe-options-expiration-select" aria-label="Filter options by expiration">
      ${values
        .map(
          (value, i) =>
            `<option value="${value}"${value === expirationFilter ? " selected" : ""}>${labels[i]}</option>`,
        )
        .join("")}
    </select>`;
  const select = host.querySelector("select");
  if (select instanceof HTMLSelectElement) applySelect(select);
  return host;
}

function buildExpirationFilterFromNative(sectorTemplate: HTMLElement | null): HTMLElement {
  return buildExpirationSelectFilter(sectorTemplate);
}

function buildRiskFilterFromNative(template: HTMLElement | null): HTMLElement {
  const values = ["all", "risky"] as const;
  const labels = ["All", "Risky"] as const;
  return buildSegmentFilterHost(
    RISK_FILTER_ID,
    "sfe-options-risk-filter",
    "Risk",
    RISK_TIP,
    "Filter options by assignment risk",
    "risk-filter",
    values,
    labels,
    riskFilter,
    (value) => {
      riskFilter = value === "risky" ? "risky" : "all";
      scheduleSync();
    },
    template,
  );
}
function findNativeOptionsFilterGroups(table: HTMLTableElement): {
  row: HTMLElement;
  sectorGroup: HTMLElement;
  moneynessGroup: HTMLElement | null;
} | null {
  const root =
    table.closest<HTMLElement>('[data-wheel-view="options"]') ??
    table.parentElement?.parentElement;
  if (!root) return null;

  const names = new Set(["TICKER", "TYPE", "MONEYNESS", "SECTOR"]);
  const labels = Array.from(root.querySelectorAll<HTMLElement>("label, span, div")).filter((el) =>
    names.has(directText(el)),
  );
  const sectorLabel = labels.find((el) => directText(el) === "SECTOR");
  const moneynessLabel = labels.find((el) => directText(el) === "MONEYNESS");
  if (!sectorLabel) return null;

  let row: HTMLElement | null = sectorLabel.parentElement;
  while (row && row !== root) {
    const count = new Set(
      labels.filter((label) => row?.contains(label)).map((label) => directText(label)),
    ).size;
    if (count >= 3) break;
    row = row.parentElement;
  }
  if (!row || row === root) return null;

  const groupFor = (label: HTMLElement): HTMLElement => {
    let group: HTMLElement = label;
    while (group.parentElement && group.parentElement !== row) {
      group = group.parentElement;
    }
    return group;
  };

  return {
    row,
    sectorGroup: groupFor(sectorLabel),
    moneynessGroup: moneynessLabel ? groupFor(moneynessLabel) : null,
  };
}

function syncExpirationSelect(host: HTMLElement): void {
  const select = host.querySelector<HTMLSelectElement>("select");
  if (select) select.value = expirationFilter;
}

function ensureExpirationFilter(table: HTMLTableElement): void {
  const nativeFilters = findNativeOptionsFilterGroups(table);
  let host = document.getElementById(EXPIRATION_FILTER_ID);

  // Migrate older segment-button Expiration control to a compact select.
  if (
    host &&
    !host.querySelector("select.sfe-options-expiration-select, select")
  ) {
    host.remove();
    host = null;
  }

  if (!host) {
    host = buildExpirationFilterFromNative(nativeFilters?.sectorGroup ?? null);
  }

  syncExpirationSelect(host);
  nativeFilters?.row.classList.add("sfe-options-filter-row");

  if (nativeFilters) {
    if (
      host.parentElement !== nativeFilters.row ||
      host.previousElementSibling !== nativeFilters.sectorGroup
    ) {
      nativeFilters.sectorGroup.insertAdjacentElement("afterend", host);
    }
  } else if (host.parentElement !== table.parentElement || host.nextElementSibling !== table) {
    table.parentElement?.insertBefore(host, table);
  }
}

function ensureRiskFilter(table: HTMLTableElement): void {
  const nativeFilters = findNativeOptionsFilterGroups(table);
  ensureExpirationFilter(table);
  const expirationHost = document.getElementById(EXPIRATION_FILTER_ID);
  let host = document.getElementById(RISK_FILTER_ID);

  if (
    host &&
    nativeFilters?.moneynessGroup &&
    host.querySelector(":scope > .sfe-options-risk-segments")
  ) {
    host.remove();
    host = null;
  }

  if (!host) {
    host = buildRiskFilterFromNative(nativeFilters?.moneynessGroup ?? null);
  }

  syncSegmentFilterButtons(host, "risk-filter", riskFilter);

  if (nativeFilters && expirationHost) {
    if (
      host.parentElement !== nativeFilters.row ||
      host.previousElementSibling !== expirationHost
    ) {
      expirationHost.insertAdjacentElement("afterend", host);
    }
  } else if (expirationHost) {
    if (host.parentElement !== expirationHost.parentElement || host.previousElementSibling !== expirationHost) {
      expirationHost.insertAdjacentElement("afterend", host);
    }
  } else if (host.parentElement !== table.parentElement || host.nextElementSibling !== table) {
    table.parentElement?.insertBefore(host, table);
  }
}

function isExpiredPosition(position: OptionPosition | undefined): boolean {
  if (!position) return false;
  if (position.expired) return true;
  if (!position.exp_date) return false;
  return position.exp_date < new Date().toLocaleDateString("en-CA");
}

function daysUntilExpiration(position: OptionPosition): number | null {
  const dte = Number(position.dte);
  if (Number.isFinite(dte)) return dte;

  if (!position.exp_date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(`${position.exp_date}T00:00:00`);
  if (Number.isNaN(exp.getTime())) return null;
  return Math.round((exp.getTime() - today.getTime()) / 86_400_000);
}

function isAtExpiration(position: OptionPosition | undefined): boolean {
  if (!position || isExpiredPosition(position)) return false;
  const dte = daysUntilExpiration(position);
  return dte != null && dte >= 0 && dte < 3;
}

function applyExpirationFilter(
  row: HTMLTableRowElement,
  bySymbol: Map<string, OptionPosition>,
): void {
  const position = bySymbol.get(row.getAttribute("data-symbol") ?? "");
  const visible =
    expirationFilter === "all" ||
    (expirationFilter === "expired" && isExpiredPosition(position)) ||
    (expirationFilter === "at-expiration" && isAtExpiration(position));
  row.classList.toggle(
    EXPIRATION_FILTER_HIDDEN,
    !visible,
  );
}

function isRiskyCallRow(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
  bySymbol: Map<string, OptionPosition>,
  stockMap: Map<string, number>,
): boolean {
  if (!rowIsCall(row, table, bySymbol)) return false;
  const sym = row.getAttribute("data-symbol");
  const position = sym ? bySymbol.get(sym) : undefined;
  const ticker = rowTicker(row, table, bySymbol);
  const avgCost = stockMap.get(ticker);
  return callStrikeVsAvgCost(position?.strike, avgCost) === "below";
}

function applyRiskFilter(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
  bySymbol: Map<string, OptionPosition>,
  stockMap: Map<string, number>,
): void {
  const visible =
    riskFilter === "all" ||
    isRiskyCallRow(row, table, bySymbol, stockMap);
  row.classList.toggle(RISK_FILTER_HIDDEN, !visible);
}

function hideOptionsDteColumn(table: HTMLTableElement): void {
  if (isWebCompactMode()) return;

  const dteHeader = headerRow(table)?.querySelector<HTMLElement>(
    'th[data-col="dte"], th[data-sort="dte"]',
  );
  if (dteHeader) dteHeader.style.display = "none";

  const hIdx = headerColumnIndex(table, "dte");
  if (hIdx < 0) return;

  for (const tr of table.querySelectorAll("tbody tr")) {
    if (!(tr instanceof HTMLTableRowElement) || isPositionsPlaceholderRow(tr)) continue;
    const cell = rowCells(tr)[bodyColumnIndex(table, tr, hIdx)];
    if (cell) cell.style.display = "none";
  }
}

export async function loadOptionsPositionsBySymbol(
  force = false,
): Promise<Map<string, OptionPosition>> {
  const snapshot = await loadPositionsSnapshot(force);
  return positionsByOptionSymbol(snapshot.options);
}

function rowIsCall(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
  bySymbol: Map<string, OptionPosition>,
): boolean {
  const sym = row.getAttribute("data-symbol");
  if (sym) {
    const pos = bySymbol.get(sym);
    if (pos) {
      const side = (pos.side ?? "").toLowerCase();
      return side === "call" || side === "c";
    }
  }
  return isCallRowDom(row, table);
}

function rowTicker(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
  bySymbol: Map<string, OptionPosition>,
): string {
  const sym = row.getAttribute("data-symbol");
  if (sym) {
    const pos = bySymbol.get(sym);
    if (pos?.ticker) return normalizeTicker(pos.ticker);
    const fromOcc = tickerFromOptionSymbol(sym);
    if (fromOcc) return normalizeTicker(fromOcc);
  }
  return normalizeTicker(parseRowTicker(row, table));
}

export function parseOptionsRowTicker(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
  bySymbol?: Map<string, OptionPosition>,
): string {
  return rowTicker(row, table, bySymbol ?? new Map());
}

export function optionsPositionFromRow(
  row: HTMLTableRowElement,
  bySymbol: Map<string, OptionPosition>,
): OptionPosition | null {
  const sym = row.getAttribute("data-symbol");
  if (!sym) return null;
  return bySymbol.get(sym) ?? null;
}

const STRIKE_TONE_CLASSES = ["roi-loss", "roi-strong", "sfe-call-strike-at-avg"] as const;
const STRIKE_TIP_CLASSES = ["sfe-data-tip-host"] as const;

function paintAvgCostCell(cell: HTMLTableCellElement, value: number | undefined): void {
  cell.textContent = value != null ? fmtUsd(value) : "—";
  cell.dataset.sfeAvgCost = value != null ? String(value) : "";
}

function paintCallCollateralCell(cell: HTMLTableCellElement): void {
  cell.innerHTML = '<span class="sfe-collateral-na" aria-hidden="true">—</span>';
  cell.classList.add("sfe-call-collateral-na");
  cell.removeAttribute("data-tip");
  cell.removeAttribute("title");
}

function removeInjectedPopColumn(table: HTMLTableElement): void {
  table.querySelectorAll(`.${POP_HEADER}, .${POP_CELL}`).forEach((el) => el.remove());
}

function resolvePositionPop(
  position: OptionPosition | undefined,
  rec: WheelRecommendation | undefined,
  allowMarketdata: boolean,
): number | null | undefined {
  const screenerPop = rec?.pop;
  if (screenerPop != null && Number.isFinite(screenerPop)) {
    return screenerPop;
  }

  const optionSymbol = position?.option_symbol;
  if (allowMarketdata && optionSymbol) {
    const cached = getCachedMarketdataOptionPop(optionSymbol);
    if (cached !== undefined) return cached;
  }

  return undefined;
}

function collectMissingMarketdataPopSymbols(
  rows: HTMLTableRowElement[],
  table: HTMLTableElement,
  bySymbol: Map<string, OptionPosition>,
  recLookup: Map<string, WheelRecommendation>,
): string[] {
  const missing: string[] = [];
  const seen = new Set<string>();

  for (const tr of rows) {
    const sym = tr.getAttribute("data-symbol");
    const position = sym ? bySymbol.get(sym) : undefined;
    const rec = position ? matchPositionToRecommendation(position, recLookup) : undefined;
    if (rec?.pop != null && Number.isFinite(rec.pop)) continue;

    const optionSymbol = position?.option_symbol;
    if (!optionSymbol) continue;

    const normalized = optionSymbol.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!normalized || seen.has(normalized)) continue;
    if (getCachedMarketdataOptionPop(normalized) !== undefined) continue;

    seen.add(normalized);
    missing.push(normalized);
  }

  return missing;
}

function paintCallStrikeCell(
  cell: HTMLTableCellElement,
  strike: number | undefined,
  avgCost: number | undefined,
  contracts?: number,
  pop?: number | null,
): void {
  cell.classList.remove(...STRIKE_TONE_CLASSES, ...STRIKE_TIP_CLASSES);
  cell.querySelector(".sfe-data-tip-popover")?.remove();
  cell.querySelector(".sfe-strike-pop")?.remove();

  // Puts / reset: strip call strike decorations; still show POP when known.
  if (strike == null && avgCost == null) {
    cell.removeAttribute("data-tip");
    cell.removeAttribute("aria-label");
    cell.removeAttribute("title");
    if (pop != null && Number.isFinite(pop)) {
      const small = document.createElement("small");
      small.className = "sfe-strike-pop";
      small.textContent = `POP: ${fmtWheelRatio(pop)}`;
      cell.appendChild(small);
    }
    return;
  }

  const vs = callStrikeVsAvgCost(strike, avgCost);
  const toneClass = callStrikeVsAvgValueClass(vs);
  if (toneClass) cell.classList.add(toneClass);
  const tipHostClass = callStrikeTipHostClass(strike, avgCost);
  const shares =
    contracts != null && Number.isFinite(contracts) && contracts > 0
      ? Math.round(contracts * 100)
      : undefined;
  const tip = callStrikeVsAvgTip(strike, avgCost, shares);
  const popover = renderCallStrikeTipPopover(strike, avgCost, shares);
  if (tipHostClass) {
    for (const cls of tipHostClass.split(/\s+/)) {
      if (cls) cell.classList.add(cls);
    }
  }

  // Second line under strike when POP is known for this open leg.
  if (pop != null && Number.isFinite(pop)) {
    const small = document.createElement("small");
    small.className = "sfe-strike-pop";
    small.textContent = `POP: ${fmtWheelRatio(pop)}`;
    cell.appendChild(small);
  }

  if (popover) {
    cell.insertAdjacentHTML("beforeend", popover);
    if (tip) cell.setAttribute("aria-label", tip);
    cell.removeAttribute("data-tip");
  } else {
    cell.removeAttribute("data-tip");
    cell.removeAttribute("aria-label");
  }
  cell.removeAttribute("title");
}

export async function syncOptionsPositionsAvgCost(force = false): Promise<void> {
  if (applying) return;
  const table = findWheelPositionsTable();
  if (!table) return;

  const strikeHeader = findStrikeHeader(table);
  const theadRow = headerRow(table);
  if (!strikeHeader || !theadRow) return;

  applying = true;
  try {
    ensureRiskFilter(table);
    let avgHeader = table.querySelector(`.${AVG_COST_HEADER}`);
    if (!avgHeader) {
      const th = document.createElement("th");
      th.className = `num ${AVG_COST_HEADER}`;
      th.setAttribute("data-col", "sfe_holding_avg_cost");
      th.setAttribute("data-tip", "Average share cost basis for covered calls.");
      th.textContent = "Avg cost";
      theadRow.insertBefore(th, strikeHeader);
    } else if (avgHeader.nextElementSibling !== strikeHeader) {
      theadRow.insertBefore(avgHeader, strikeHeader);
    }

    let riskOptyHeader = table.querySelector(`.${RISK_OPTY_HEADER}`);
    if (!riskOptyHeader) {
      const th = document.createElement("th");
      th.className = `num ${RISK_OPTY_HEADER}`;
      th.setAttribute("data-col", "sfe_risk_opty");
      th.setAttribute(
        "data-tip",
        "Calls: dollars at risk if strike is below avg share cost, or opportunity if strike is above. Puts: —.",
      );
      th.textContent = "Risk / opty";
      strikeHeader.insertAdjacentElement("afterend", th);
    } else if (strikeHeader.nextElementSibling !== riskOptyHeader) {
      strikeHeader.insertAdjacentElement("afterend", riskOptyHeader);
    }

    // POP now lives under Strike for open calls — drop the old injected column.
    removeInjectedPopColumn(table);

    // Use the shared 60s snapshot cache — forcing on every DOM mutation
    // was stampeding Stock Forge whenever the native positions table re-rendered.
    const [stockMap, positionsSnap, recsSnap] = await Promise.all([
      buildShareAvgByTicker(force),
      loadPositionsSnapshot(force),
      loadRecommendationsSnapshot(force),
    ]);
    const bySymbol = positionsByOptionSymbol(positionsSnap.options);
    const recLookup = buildRecommendationLookup(recsSnap);

    const dataRows = [...table.querySelectorAll("tbody tr")].filter(
      (tr): tr is HTMLTableRowElement =>
        tr instanceof HTMLTableRowElement && !isPositionsPlaceholderRow(tr),
    );
    const missingPopSymbols = collectMissingMarketdataPopSymbols(
      dataRows,
      table,
      bySymbol,
      recLookup,
    );
    if (missingPopSymbols.length) {
      for (let i = 0; i < missingPopSymbols.length; i += MARKETDATA_POP_BATCH_SIZE) {
        await fetchMarketdataOptionPopBatch(
          missingPopSymbols.slice(i, i + MARKETDATA_POP_BATCH_SIZE),
        );
      }
    }

    hideOptionsDteColumn(table);

    for (const tr of dataRows) {

      applyExpirationFilter(tr, bySymbol);
      applyRiskFilter(tr, table, bySymbol, stockMap);
      const avgCell = ensureAvgCostCell(tr, table);
      if (!avgCell) continue;
      const riskOptyCell = ensureRiskOptyCell(tr, table);

      const strikeCell = findBodyStrikeCell(tr, table);
      const collateralCell = findBodyCellBySort(tr, table, "collateral");
      const sym = tr.getAttribute("data-symbol");
      const position = sym ? bySymbol.get(sym) : undefined;
      const rec = position ? matchPositionToRecommendation(position, recLookup) : undefined;
      const isCall = rowIsCall(tr, table, bySymbol);
      const pop = resolvePositionPop(position, rec, true);
      if (isCall) {
        const ticker = rowTicker(tr, table, bySymbol);
        const avgCost = stockMap.get(ticker);
        paintAvgCostCell(avgCell, avgCost);
        if (strikeCell) {
          paintCallStrikeCell(strikeCell, position?.strike, avgCost, position?.contracts, pop);
        }
        if (riskOptyCell) {
          paintRiskOptyCell(riskOptyCell, "call", position?.strike, avgCost, position?.contracts);
        }
        if (collateralCell) paintCallCollateralCell(collateralCell);
      } else {
        paintAvgCostCell(avgCell, undefined);
        if (strikeCell) paintCallStrikeCell(strikeCell, undefined, undefined, undefined, pop);
        if (riskOptyCell) paintRiskOptyCell(riskOptyCell, "put", undefined, undefined, undefined);
      }

      if (!isWebCompactMode() && position) {
        const expiryCell =
          findBodyCellBySort(tr, table, "expiration") ??
          tr.querySelector<HTMLTableCellElement>('td[data-col="expiration"]');
        if (expiryCell) {
          paintScreenerExpiryCell(expiryCell, {
            expiration_date: position.exp_date,
            dte: daysUntilExpiration(position),
          });
        }
      }
    }
  } finally {
    applying = false;
    syncResetButtons();
  }
}

function scheduleSync(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void syncOptionsPositionsAvgCost();
  }, 100);
}

function observePositionsTable(table: HTMLTableElement): void {
  domObserver?.disconnect();
  domObserver = new MutationObserver(() => scheduleSync());
  domObserver.observe(table, { childList: true, subtree: true });

  const body = document.getElementById("wheelPositionsBody");
  if (body) domObserver.observe(body, { childList: true, subtree: true });
}

let ledgerChangedListenerBound = false;

export function mountOptionsPositionsAvgCost(): void {
  void syncOptionsPositionsAvgCost();

  if (!ledgerChangedListenerBound) {
    ledgerChangedListenerBound = true;
    document.addEventListener(WHEEL_LEDGER_CHANGED_EVENT, () => {
      void syncOptionsPositionsAvgCost(true);
    });
  }

  const table = findWheelPositionsTable();
  if (table) observePositionsTable(table);

  const bodyObserver = new MutationObserver(() => {
    const next = findWheelPositionsTable();
    if (next) {
      observePositionsTable(next);
      scheduleSync();
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });
}
